import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { 
  Baby, Piano, Guitar, Mic, Sparkles, 
  Clock, Users, CheckCircle, ArrowRight, Phone
} from 'lucide-react';

// Image URLs - Custom photos from École de Musique Pour Tous
const IMAGES = {
  eveil: "https://customer-assets-7cd3h4nn.emergentagent.net/job_cours-piano-guitare/artifacts/kbd2vv9u_1788262630972.jpg",
  piano: "https://customer-assets-7cd3h4nn.emergentagent.net/job_cours-piano-guitare/artifacts/nkdr68d1_1788262577714.jpg",
  guitar: "https://customer-assets-7cd3h4nn.emergentagent.net/job_cours-piano-guitare/artifacts/i7pa1aiq_1788262603278.jpg",
  singing: "https://customer-assets-7cd3h4nn.emergentagent.net/job_cours-piano-guitare/artifacts/4085hbst_1788262380744.jpg",
  sensory: "https://customer-assets-7cd3h4nn.emergentagent.net/job_cours-piano-guitare/artifacts/y6ueppwi_1788262562018.jpg",
};

// Schema.org structured data for courses
const coursesSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "Cours de musique à Périgueux",
  "description": "Cours de piano, guitare, chant et éveil musical à Périgueux pour enfants et adultes",
  "itemListElement": [
    {
      "@type": "Course",
      "position": 1,
      "name": "Éveil Musical à Périgueux",
      "description": "Cours d'éveil musical pour enfants de 3 à 8 ans à Périgueux. Découverte des sons, rythmes et instruments.",
      "provider": {
        "@type": "MusicSchool",
        "name": "École de Musique Pour Tous",
        "address": { "@type": "PostalAddress", "addressLocality": "Périgueux", "postalCode": "24000" }
      }
    },
    {
      "@type": "Course",
      "position": 2,
      "name": "Cours de Piano à Périgueux",
      "description": "Cours de piano individuels à Périgueux pour débutants et confirmés, enfants et adultes.",
      "provider": {
        "@type": "MusicSchool",
        "name": "École de Musique Pour Tous",
        "address": { "@type": "PostalAddress", "addressLocality": "Périgueux", "postalCode": "24000" }
      }
    },
    {
      "@type": "Course",
      "position": 3,
      "name": "Cours de Guitare à Périgueux",
      "description": "Cours de guitare acoustique et classique à Périgueux pour tous niveaux.",
      "provider": {
        "@type": "MusicSchool",
        "name": "École de Musique Pour Tous",
        "address": { "@type": "PostalAddress", "addressLocality": "Périgueux", "postalCode": "24000" }
      }
    },
    {
      "@type": "Course",
      "position": 4,
      "name": "Cours de Chant à Périgueux",
      "description": "Cours de chant et technique vocale à Périgueux pour développer votre voix.",
      "provider": {
        "@type": "MusicSchool",
        "name": "École de Musique Pour Tous",
        "address": { "@type": "PostalAddress", "addressLocality": "Périgueux", "postalCode": "24000" }
      }
    }
  ]
};

const courses = [
  {
    id: "eveil",
    title: "Éveil Musical",
    subtitle: "Pour les 3-8 ans",
    icon: Baby,
    image: IMAGES.eveil,
    color: "coral",
    description: "L'éveil musical est une aventure sensorielle où votre enfant découvre le monde des sons. À travers des chansons, des jeux rythmiques et la manipulation d'instruments adaptés, les enfants développent leur écoute, leur coordination et leur créativité.",
    benefits: [
      "Développement de l'écoute et de l'attention",
      "Découverte des instruments de musique",
      "Comptines et chansons adaptées",
      "Éveil corporel et coordination",
      "Socialisation en groupe (max 7-8 élèves)"
    ],
    details: {
      duration: "30 à 45 minutes",
      format: "Individuel ou collectif",
      age: "3 à 8 ans"
    }
  },
  {
    id: "piano",
    title: "Piano",
    subtitle: "Dès 5 ans, ados et adultes",
    icon: Piano,
    image: IMAGES.piano,
    color: "burgundy",
    description: "Le piano est l'instrument roi pour commencer la musique. Charlotte et sa collègue accompagnent chaque élève avec une méthode progressive et personnalisée. Du classique au moderne, le répertoire s'adapte à vos goûts.",
    benefits: [
      "Cours dispensés par Charlotte et sa collègue",
      "Apprentissage de la lecture de notes",
      "Technique pianistique progressive",
      "Répertoire varié et personnalisé",
      "Préparation aux examens (optionnel)"
    ],
    details: {
      duration: "30 à 60 minutes",
      format: "Individuel ou collectif",
      age: "Dès 5 ans"
    }
  },
  {
    id: "guitare",
    title: "Guitare",
    subtitle: "Dès 6 ans, ados et adultes",
    icon: Guitar,
    image: IMAGES.guitar,
    color: "teal",
    description: "La guitare acoustique est l'instrument idéal pour accompagner ses chansons préférées. Que vous rêviez de jouer du folk, du pop ou du classique, notre équipe vous accompagne dans la maîtrise des accords, du picking et de la technique guitaristique.",
    benefits: [
      "Apprentissage des accords de base",
      "Technique main droite et main gauche",
      "Accompagnement de chansons",
      "Lecture de tablatures",
      "Répertoire adapté à vos goûts"
    ],
    details: {
      duration: "30 à 60 minutes",
      format: "Individuel ou collectif",
      age: "Dès 6 ans"
    }
  },
  {
    id: "chant",
    title: "Chant",
    subtitle: "Dès 7 ans, ados et adultes",
    icon: Mic,
    image: IMAGES.singing,
    color: "coral",
    description: "Votre voix est votre instrument le plus personnel. Les cours de chant vous permettent de la découvrir, de la développer et de l'exprimer. Technique vocale, respiration, interprétation : tout est abordé dans une ambiance bienveillante par notre équipe.",
    benefits: [
      "Technique vocale et respiration",
      "Travail de la justesse",
      "Interprétation et expression",
      "Répertoire varié (variété, comédie musicale...)",
      "Gestion du trac"
    ],
    details: {
      duration: "30 à 60 minutes",
      format: "Individuel ou collectif",
      age: "Dès 7 ans"
    }
  },
  {
    id: "ateliers",
    title: "Ateliers Sensoriels",
    subtitle: "Atelier bébé-parent dès 3 mois",
    icon: Sparkles,
    image: IMAGES.sensory,
    color: "teal",
    description: "Les ateliers sensoriels sont une approche globale où la musique rencontre les sens. Parents et bébés partagent un moment privilégié autour du son, du toucher et du mouvement. Une expérience unique de lien et de découverte proposée par notre équipe.",
    benefits: [
      "Moment de partage parent-enfant",
      "Stimulation sensorielle",
      "Découverte des sons et textures",
      "Développement de la motricité",
      "Éveil à la musique dès le plus jeune âge"
    ],
    details: {
      duration: "30 à 45 minutes",
      format: "Atelier parent-enfant",
      age: "Dès 3 mois"
    }
  }
];

const CourseSection = ({ course, index }) => {
  const colorClasses = {
    burgundy: {
      bg: 'bg-burgundy',
      bgLight: 'bg-burgundy/10',
      text: 'text-burgundy',
      border: 'border-burgundy'
    },
    coral: {
      bg: 'bg-coral',
      bgLight: 'bg-coral/10',
      text: 'text-coral',
      border: 'border-coral'
    },
    teal: {
      bg: 'bg-teal',
      bgLight: 'bg-teal/10',
      text: 'text-teal',
      border: 'border-teal'
    }
  };

  const colors = colorClasses[course.color];
  const Icon = course.icon;
  const isEven = index % 2 === 0;

  return (
    <section
      id={course.id}
      className={`py-20 ${index % 2 === 0 ? 'bg-white' : 'bg-cream'}`}
      data-testid={`course-section-${course.id}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}>
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: isEven ? -30 : 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={`relative ${!isEven ? 'lg:order-2' : ''}`}
          >
            <div className={`absolute -top-4 ${isEven ? '-left-4' : '-right-4'} w-full h-full ${colors.bgLight} rounded-3xl`} />
            <img
              src={course.image}
              alt={course.title}
              className={`relative rounded-3xl shadow-xl w-full ${course.id === 'eveil' ? 'h-auto object-contain' : 'h-80 lg:h-96 object-cover'}`}
              loading="lazy"
            />
            <div className={`absolute top-4 ${isEven ? 'left-4' : 'right-4'} w-14 h-14 ${colors.bg} rounded-full flex items-center justify-center shadow-lg`}>
              <Icon className="w-7 h-7 text-white" />
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: isEven ? 30 : -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={!isEven ? 'lg:order-1' : ''}
          >
            <span className={`inline-block px-4 py-2 ${colors.bgLight} ${colors.text} rounded-full text-sm font-semibold mb-4`}>
              {course.subtitle}
            </span>
            <h2 className="font-dancing text-4xl sm:text-5xl text-burgundy mb-4">
              {course.title}
            </h2>
            <p className="text-gray-600 mb-6 leading-relaxed font-nunito">
              {course.description}
            </p>

            {/* Details */}
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex items-center gap-2 text-gray-600 text-sm bg-gray-100 px-3 py-2 rounded-lg">
                <Clock className="w-4 h-4" />
                <span>{course.details.duration}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 text-sm bg-gray-100 px-3 py-2 rounded-lg">
                <Users className="w-4 h-4" />
                <span>{course.details.format}</span>
              </div>
            </div>

            {/* Benefits */}
            <ul className="space-y-3 mb-8">
              {course.benefits.map((benefit, i) => (
                <li key={i} className="flex items-start gap-3 text-gray-700">
                  <div className={`w-5 h-5 ${colors.bgLight} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`}>
                    <CheckCircle className={`w-3 h-3 ${colors.text}`} />
                  </div>
                  <span className="font-nunito text-sm">{benefit}</span>
                </li>
              ))}
            </ul>

            <Link
              to="/contact"
              className={`inline-flex items-center gap-2 px-6 py-3 ${colors.bg} text-white rounded-full font-semibold hover:opacity-90 transition-all`}
              data-testid={`course-cta-${course.id}`}
            >
              Réserver un essai
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default function CoursesPage() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const targetId = location.hash.slice(1);
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        setTimeout(() => {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen pt-20" data-testid="courses-page">
      <Helmet>
        <title>Cours de Piano, Guitare, Chant et Éveil Musical à Périgueux | École de Musique Pour Tous</title>
        <meta name="description" content="Découvrez nos cours de piano, guitare, chant et éveil musical à Périgueux. Cours individuels et collectifs pour enfants et adultes, tous niveaux. Premier cours d'essai gratuit !" />
        <meta name="keywords" content="cours piano périgueux, cours guitare périgueux, cours chant périgueux, éveil musical périgueux, école musique périgueux, cours musique dordogne" />
        <link rel="canonical" href="https://ecoledemusiqueperigueux.fr/cours" />
        <script type="application/ld+json">
          {JSON.stringify(coursesSchema)}
        </script>
      </Helmet>
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-cream to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block px-4 py-2 bg-burgundy/10 text-burgundy rounded-full text-sm font-semibold mb-4">
              Nos Cours à Périgueux
            </span>
            <h1 className="font-dancing text-5xl sm:text-6xl text-burgundy mb-6">
              Cours de musique à Périgueux
            </h1>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto font-nunito">
              Cours de piano, guitare, chant et éveil musical à Périgueux pour enfants et adultes. 
              Charlotte et son équipe vous accompagnent dans votre apprentissage musical, quel que soit votre niveau.
            </p>
          </motion.div>

          {/* Quick Navigation */}
          <div className="flex flex-wrap justify-center gap-3 mt-10">
            {courses.map((course) => {
              const Icon = course.icon;
              return (
                <a
                  key={course.id}
                  href={`#${course.id}`}
                  className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm hover:shadow-md transition-all text-sm font-semibold text-gray-700 hover:text-burgundy"
                  data-testid={`course-nav-${course.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {course.title}
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Course Sections */}
      {courses.map((course, index) => (
        <CourseSection key={course.id} course={course} index={index} />
      ))}

      {/* Final CTA */}
      <section className="py-20 bg-burgundy text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="font-dancing text-4xl sm:text-5xl mb-6">
              Envie de commencer ?
            </h2>
            <p className="text-white/80 text-lg mb-8 font-nunito">
              Réservez votre cours d'essai gratuit et découvrez quelle discipline vous correspond le mieux !
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-burgundy rounded-full font-bold hover:bg-gray-100 transition-all"
                data-testid="courses-final-cta"
              >
                Réserver un essai gratuit
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="tel:+33649523778"
                className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white rounded-full font-bold hover:bg-white hover:text-burgundy transition-all"
                data-testid="courses-phone-btn"
              >
                <Phone className="w-5 h-5" />
                06 49 52 37 78
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
